import { submitForm, useAppForm } from "@birdwatch/ui/form";
import { toast } from "@birdwatch/ui/toast";
import { useStore } from "@tanstack/react-form";
import { Extension } from "@tiptap/core";
import { useCallback, useMemo, useRef } from "react";
import type { EditorContentHandle } from "@/components/ui/editor/rich-text";
import { useTypingStatus } from "./_hooks/use-typing-status";
import {
  buildChatComposerSchema,
  EMPTY_CHAT_COMPOSER_VALUES,
  getMessageLengthError,
} from "./_libs/chat-composer-schema";
import type {
  ChatComposerApi,
  ChatComposerConfig,
  ChatComposerValues,
} from "./_types/chat-composer";

export function useChatComposerForm(
  config: ChatComposerConfig,
): ChatComposerApi {
  const {
    source,
    onSend,
    errorTitle,
    errorDescription,
    typing,
    onSent,
    onFailedSendRestored,
    onBeforeSend,
    requireDirty = false,
    allowEmpty = false,
    maxCharacters,
    sendMode = "await",
  } = config;

  const editorRef = useRef<EditorContentHandle | null>(null);
  const uploadRef = useRef<any>(null);
  const audioRecorderRef = useRef<any>(null);
  const uploadTriggerRef = useRef<any>(null);

  const draft = source.kind === "draft" ? source.draft : undefined;

  /**
   * The form's CURRENT defaults, not just its initial ones.
   *
   * react-form re-applies `update(opts)` in a layout effect on EVERY render, and
   * `update` overwrites `values` whenever `defaultValues` is deep-unequal to the
   * stored defaults AND no field is touched — precisely the state `form.reset()`
   * leaves behind. So this ref must advance with every reset, or the post-send
   * clear is undone one render later: `content` returns to the mount text behind
   * an already-emptied editor, leaving Send enabled and a second press posting a
   * duplicate.
   */
  const defaultValuesRef = useRef<ChatComposerValues>(
    source.kind === "values"
      ? source.values
      : {
          content: draft?.read()?.content ?? "",
          attachments: draft?.read()?.attachments ?? [],
          audios: draft?.read()?.audios ?? [],
        },
  );

  const schema = useMemo(
    () => buildChatComposerSchema({ maxCharacters, allowEmpty }),
    [maxCharacters, allowEmpty],
  );

  const form = useAppForm({
    defaultValues: defaultValuesRef.current as any,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      const values = value as ChatComposerValues;
      // `"instant"` clears at PRESS time, in one synchronous pass, and never
      // touches the composer again. Clearing after the await instead is what
      // produced the send-button bug: the box emptied at press, the form reset
      // when the request finished, and anything typed in between survived in the
      // uncontrolled editor but not in the form — visible text, disabled Send.
      // The `form.reset` inside `clearInputs` also drops `isSubmitting`, which is
      // what hands the box straight back for the next message.
      if (sendMode === "instant") {
        clearInputs();
        // "Sent" is now, not a round trip from now. The thread's own placeholder
        // lands a tick later, and its container is `flex-col-reverse` — scrolling
        // to the bottom first leaves the reader pinned there as the bubble arrives,
        // whereas waiting would yank the view while they type the next message.
        onSent?.();
      }
      try {
        await onSend(values);
        if (sendMode === "await") {
          clearInputs();
          onSent?.();
        }
      } catch (error) {
        restoreFailedSend(values);
        toast.error({
          title: errorTitle,
          description: errorDescription?.(error),
        });
      }
    },
  });

  /**
   * Empty every input the composer owns.
   *
   * The editor goes first and `form.reset` last: `resetContent` emits an update
   * that synchronously writes `""` into the form field and marks it touched, so
   * the reset has to be the final word on the form state.
   */
  const clearInputs = useCallback(() => {
    editorRef.current?.resetContent();
    uploadRef.current?.reset();
    audioRecorderRef.current?.reset();
    // Advance the defaults BEFORE the reset — see `defaultValuesRef`.
    defaultValuesRef.current = EMPTY_CHAT_COMPOSER_VALUES;
    form.reset(defaultValuesRef.current as any);
    draft?.clear();
  }, [form, draft]);

  // `useStore`'s default comparator is `===` — one selector per primitive,
  // NEVER an object literal, or every keystroke re-renders regardless of what
  // changed.
  const content = useStore(
    form.store,
    (s) => ((s.values as any).content as string) ?? "",
  );
  const attachments = useStore(
    form.store,
    (s) => ((s.values as any).attachments ?? []) as any[],
  );
  const audios = useStore(
    form.store,
    (s) => ((s.values as any).audios ?? []) as any[],
  );
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
  const canSubmit = useStore(form.store, (s) => s.canSubmit);
  const isDirty = useStore(form.store, (s) => s.isDirty);

  // Typing status — called unconditionally in the hook BODY, NEVER inside a
  // `form.Subscribe` render prop (that is a rules-of-hooks violation).
  const { stopTyping } = useTypingStatus({
    enabled: Boolean(typing),
    value: content,
    cid: typing?.cid ?? "",
  });

  const submit = useCallback(() => {
    void submitForm(form as never);
  }, [form]);

  const requestSend = useCallback(() => {
    // The form's live values: this callback does not close over `content`.
    const values = form.state.values as ChatComposerValues;
    if (onBeforeSend && onBeforeSend(values) === false) return;
    submit();
  }, [onBeforeSend, submit, form]);

  // Stable ref so the Tiptap keyboard shortcut (baked into the extension list
  // once, at editor construction) always calls the current handler.
  const requestSendRef = useRef(requestSend);
  requestSendRef.current = requestSend;

  const keyboardExtensions = useMemo(
    () => [
      Extension.create({
        name: "chatComposerSend",
        // Above the editor's HardBreak (100), whose own Mod-Enter inserts a line
        // break. Shift-Enter is left to HardBreak.
        priority: 101,
        addKeyboardShortcuts() {
          return {
            "Mod-Enter": ({ editor }) => {
              if (!editor.isEmpty) requestSendRef.current();
              return true;
            },
          };
        },
      }),
    ],
    [],
  );

  const setContent = useCallback(
    (value: string) => {
      form.setFieldValue("content", value);
      editorRef.current?.setContent(value);
    },
    [form],
  );

  /**
   * Put a rejected message back — but only into a box the user has left alone.
   *
   * Under `"await"` nothing was cleared, so there is nothing to do. Under
   * `"instant"` the box was emptied at press time and the user may already be
   * typing the next message; overwriting that to recover the failed one trades a
   * known loss for a surprising one, so the toast is the whole answer there.
   */
  const restoreFailedSend = useCallback(
    (values: ChatComposerValues) => {
      if (sendMode !== "instant") return;
      // Read live state, not the render closure — this runs after an await.
      const current = form.state.values as ChatComposerValues;
      const isBoxUntouched =
        (editorRef.current?.isEmpty() ?? true) &&
        (current.attachments?.length ?? 0) === 0 &&
        (current.audios?.length ?? 0) === 0;
      if (!isBoxUntouched) return;

      setContent(values.content);
      // Republishes through the uploader's own onChange, which also rewrites the
      // draft. Audios have no such path, hence the explicit draft write.
      uploadRef.current?.addAttachments(values.attachments);
      form.setFieldValue("audios", values.audios);
      draft?.setAudios?.(values.audios);
      onFailedSendRestored?.();
    },
    [sendMode, form, setContent, draft, onFailedSendRestored],
  );

  // Surfaced rather than left to the form's error display: `isDisabled` below
  // keeps the send button disabled while the body is over the limit, and a
  // disabled button never produces the submit attempt that display gate waits
  // for — so without this the user gets a dead button and no reason for it.
  const limitError = getMessageLengthError(content, maxCharacters);

  const isUploading = attachments.some(
    (a: any) => a?.status != null && a.status !== "done",
  );
  const isUploadingAudio = audios.some((a: any) => a?.status === "uploading");
  const hasContent =
    content.trim().length > 0 || attachments.length > 0 || audios.length > 0;
  // An instant composer has nothing pending: it hands the message off at press
  // time, and the form's own `isSubmitting` is a transient the press-time reset
  // clears in the same tick. Reporting it as settled keeps every surface from
  // rendering a spinner or a disabled button off a flag that is already stale by
  // the time it paints.
  const isSendPending = sendMode === "await" && isSubmitting;
  const isDisabled =
    (!hasContent && !allowEmpty) ||
    !canSubmit ||
    isSendPending ||
    isUploading ||
    isUploadingAudio ||
    (requireDirty && !isDirty);

  return {
    form,
    editorRef,
    uploadRef,
    audioRecorderRef,
    uploadTriggerRef,
    content,
    attachments,
    audios,
    isSubmitting: isSendPending,
    isUploading: isUploading || isUploadingAudio,
    isDisabled,
    limitError,
    submit,
    requestSend,
    setContent,
    keyboardExtensions,
    draft,
    uploadScope: draft ? `composer:${draft.key}` : undefined,
    typingEnabled: Boolean(typing),
    stopTyping,
  };
}
